import React, { useEffect, useMemo, useRef, useState } from "react";
import { LEGEND_PORTRAITS, LEGEND_WEAPONS, WEAPON_ICONS, capitalize } from "./legendData";

// ─── Types ────────────────────────────────────────────────────────────────────
type LegendItem = { name: string; icon: string };
type WeaponItem = { name: string; icon: string };
type LegendWeaponEntry = { name: string; icon: string; weapons: [string, string] };

type CheckedMap      = Record<string, boolean>;
type ComboCheckedMap = Record<string, Record<string, boolean>>;

type ChecklistTab = "legend" | "weapon" | "combo";

// ─── Legend data ──────────────────────────────────────────────────────────────
// Derived from the shared legendData module — the same source ProfilePage uses,
// so portraits and weapon pairings can't drift out of sync between the two pages.
const legends: LegendItem[] = Object.keys(LEGEND_PORTRAITS).map((key) => ({
  name: capitalize(key),
  icon: LEGEND_PORTRAITS[key],
}));

const legendWeapons: LegendWeaponEntry[] = Object.keys(LEGEND_WEAPONS)
  .map((key) => ({
    name: capitalize(key),
    icon: LEGEND_PORTRAITS[key] || "",
    weapons: LEGEND_WEAPONS[key],
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

// ─── Weapon data ──────────────────────────────────────────────────────────────
const weapons: WeaponItem[] = Object.keys(WEAPON_ICONS).map((name) => ({
  name,
  icon: WEAPON_ICONS[name],
}));

const weaponIconLookup = WEAPON_ICONS;

// ─── Storage ──────────────────────────────────────────────────────────────────
const STORAGE_KEY_LEGEND = "brawlhalla_checklist_legends";
const STORAGE_KEY_WEAPON = "brawlhalla_checklist_weapons";
const STORAGE_KEY_COMBO  = "brawlhalla_checklist_combo";

function loadChecked<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function saveChecked(key: string, data: unknown) {
  try {
    window.localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // storage unavailable, ignore
  }
}

const tabLabels: Record<ChecklistTab, string> = {
  legend: "legends",
  weapon: "weapons",
  combo:  "legend + weapon pairs",
};

// ─── Confetti particle type ────────────────────────────────────────────────────
type Particle = {
  x: number; y: number; w: number; h: number; color: string;
  speedY: number; speedX: number; rotation: number; rotationSpeed: number;
  opacity: number; life: number; maxLife: number;
};

const confettiColors = ["#ff4d4d", "#ffb400", "#39d98a", "#0066cc", "#a259ff", "#ff66c4"];

// ─── Component-scoped styles ────────────────────────────────────────────────────
function ChecklistStyles() {
  return (
    <style>{`
      .checklist-tabs { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 20px; }
      .checklist-tab-btn.inactive { background: #2a2a2a; color: var(--text-dim); }

      .checklist-progress { max-width: 500px; }
      .checklist-progress-outer {
        background: var(--bg-input); border-radius: 8px; height: 14px; margin-top: 8px; overflow: hidden;
      }
      .checklist-progress-inner {
        background: var(--accent); height: 100%; width: 0%; transition: width 0.3s;
      }

      .checklist-grid {
        display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: 14px;
      }
      .checklist-item {
        background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md);
        padding: 12px 14px; display: flex; align-items: center; gap: 12px;
        cursor: pointer; user-select: none; transition: opacity 0.2s, background-color 0.2s;
      }
      .checklist-item.checked { background-color: rgba(57, 217, 138, 0.12); opacity: 0.7; }
      .checklist-item img { width: 42px; height: 42px; flex-shrink: 0; object-fit: cover; border-radius: 8px; }
      .checklist-item span { flex-grow: 1; font-size: 1.02em; }
      .checklist-item.checked span { text-decoration: line-through; color: var(--text-muted); }
      .checklist-item input[type="checkbox"] { width: 20px; height: 20px; flex-shrink: 0; cursor: pointer; }

      .checklist-combo-grid {
        display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 14px;
      }
      .checklist-combo-card {
        background: var(--bg-card); border: 1px solid var(--border); border-radius: var(--radius-md);
        padding: 14px 16px; transition: opacity 0.2s, background-color 0.2s;
      }
      .checklist-combo-card.complete { background-color: rgba(57, 217, 138, 0.12); opacity: 0.7; }
      .checklist-combo-header { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
      .checklist-combo-header img { width: 46px; height: 46px; object-fit: cover; border-radius: 10px; }
      .checklist-combo-header span { font-size: 1.1em; font-weight: 700; }
      .checklist-combo-card.complete .checklist-combo-header span {
        text-decoration: line-through; color: var(--text-muted);
      }
      .checklist-combo-weapon-row {
        display: flex; align-items: center; gap: 10px; padding: 4px 0 4px 6px;
        cursor: pointer; user-select: none;
      }
      .checklist-combo-weapon-row img { width: 30px; height: 30px; object-fit: contain; }
      .checklist-combo-weapon-row input[type="checkbox"] { width: 18px; height: 18px; cursor: pointer; }
      .checklist-combo-weapon-row.weaponChecked span { text-decoration: line-through; color: var(--text-muted); }

      #checklistConfettiCanvas {
        position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
        pointer-events: none; z-index: 2000;
      }
    `}</style>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function ChecklistPage({ navigateTo }: { navigateTo: (path: string) => void }) {
  const [activeTab, setActiveTab] = useState<ChecklistTab>("legend");

  const [legendChecked, setLegendChecked] = useState<CheckedMap>(() => loadChecked(STORAGE_KEY_LEGEND, {}));
  const [weaponChecked, setWeaponChecked] = useState<CheckedMap>(() => loadChecked(STORAGE_KEY_WEAPON, {}));
  const [comboChecked, setComboChecked]   = useState<ComboCheckedMap>(() => loadChecked(STORAGE_KEY_COMBO, {}));

  const [resetModalOpen, setResetModalOpen] = useState(false);

  const tabCompletedFired = useRef<Record<ChecklistTab, boolean>>({ legend: false, weapon: false, combo: false });

  const canvasRef    = useRef<HTMLCanvasElement | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animatingRef = useRef(false);

  // ── Persist to localStorage ──
  useEffect(() => { saveChecked(STORAGE_KEY_LEGEND, legendChecked); }, [legendChecked]);
  useEffect(() => { saveChecked(STORAGE_KEY_WEAPON, weaponChecked); }, [weaponChecked]);
  useEffect(() => { saveChecked(STORAGE_KEY_COMBO, comboChecked); }, [comboChecked]);

  // ── Confetti canvas sizing ──
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const fireConfetti = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const count = 180;
    const newParticles: Particle[] = [];
    for (let i = 0; i < count; i++) {
      newParticles.push({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * canvas.height * 0.4,
        w: 6 + Math.random() * 6,
        h: 8 + Math.random() * 8,
        color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
        speedY: 2 + Math.random() * 3,
        speedX: (Math.random() - 0.5) * 2,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        opacity: 1,
        life: 0,
        maxLife: 220 + Math.random() * 60,
      });
    }
    particlesRef.current = particlesRef.current.concat(newParticles);

    if (!animatingRef.current) {
      animatingRef.current = true;
      requestAnimationFrame(animateConfetti);
    }
  };

  const animateConfetti = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) { animatingRef.current = false; return; }

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particlesRef.current.forEach((p) => {
      p.life++;
      p.x += p.speedX;
      p.y += p.speedY;
      p.speedY += 0.02;
      p.rotation += p.rotationSpeed;
      if (p.life > p.maxLife * 0.7) {
        p.opacity = Math.max(0, 1 - (p.life - p.maxLife * 0.7) / (p.maxLife * 0.3));
      }

      ctx.save();
      ctx.globalAlpha = p.opacity;
      ctx.translate(p.x, p.y);
      ctx.rotate((p.rotation * Math.PI) / 180);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      ctx.restore();
    });

    particlesRef.current = particlesRef.current.filter(
      (p) => p.life < p.maxLife && p.y < canvas.height + 40
    );

    if (particlesRef.current.length > 0) {
      requestAnimationFrame(animateConfetti);
    } else {
      animatingRef.current = false;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  // ── Progress ──
  const progress = useMemo(() => {
    let total = 0;
    let done = 0;
    if (activeTab === "legend") {
      total = legends.length;
      done = legends.filter((i) => legendChecked[i.name]).length;
    } else if (activeTab === "weapon") {
      total = weapons.length;
      done = weapons.filter((i) => weaponChecked[i.name]).length;
    } else {
      total = legendWeapons.length;
      done = legendWeapons.filter((l) => {
        const state = comboChecked[l.name] || {};
        return l.weapons.every((w) => !!state[w]);
      }).length;
    }
    return { total, done };
  }, [activeTab, legendChecked, weaponChecked, comboChecked]);

  useEffect(() => {
    const { total, done } = progress;
    if (total > 0 && done === total) {
      if (!tabCompletedFired.current[activeTab]) {
        tabCompletedFired.current[activeTab] = true;
        fireConfetti();
      }
    } else {
      tabCompletedFired.current[activeTab] = false;
    }
  }, [progress, activeTab]);

  // ── Handlers ──
  const toggleLegend = (name: string) => {
    setLegendChecked((prev) => ({ ...prev, [name]: !prev[name] }));
  };
  const toggleWeapon = (name: string) => {
    setWeaponChecked((prev) => ({ ...prev, [name]: !prev[name] }));
  };
  const toggleComboWeapon = (legendName: string, weaponName: string) => {
    setComboChecked((prev) => {
      const legendState = { ...(prev[legendName] || {}) };
      legendState[weaponName] = !legendState[weaponName];
      return { ...prev, [legendName]: legendState };
    });
  };

  const confirmReset = () => {
    if (activeTab === "legend") setLegendChecked({});
    else if (activeTab === "weapon") setWeaponChecked({});
    else setComboChecked({});
    setResetModalOpen(false);
  };

  return (
    <div className="page-inner">
      <ChecklistStyles />
      <canvas id="checklistConfettiCanvas" ref={canvasRef} />

      <button onClick={() => navigateTo("/tools")} className="btn btn--secondary mb-16">
        ← Back to Tools
      </button>

      <div className="card roster-page-header">
        <div className="label-accent mb-12">QUIT</div>
        <div className="flex justify-between items-center gap-20 flex-wrap">
          <h1 style={{ margin: 0, fontSize: 46, lineHeight: 1.05 }}>Checklist</h1>
        </div>
        <p className="text-muted mb-0 mt-8">Track which legends and weapons you've played.</p>
      </div>

      <div className="checklist-tabs">
        <button
          className={`btn${activeTab === "legend" ? "" : " checklist-tab-btn inactive"}`}
          onClick={() => setActiveTab("legend")}
        >
          Legends
        </button>
        <button
          className={`btn${activeTab === "weapon" ? "" : " checklist-tab-btn inactive"}`}
          onClick={() => setActiveTab("weapon")}
        >
          Weapons
        </button>
        <button
          className={`btn${activeTab === "combo" ? "" : " checklist-tab-btn inactive"}`}
          onClick={() => setActiveTab("combo")}
        >
          Legend + Weapons
        </button>
        <button className="btn btn--danger" onClick={() => setResetModalOpen(true)}>
          Reset Checked
        </button>
      </div>

      <div className="card checklist-progress">
        <span>{progress.done} / {progress.total} checked</span>
        <div className="checklist-progress-outer">
          <div
            className="checklist-progress-inner"
            style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
          />
        </div>
      </div>

      {activeTab === "legend" && (
        <div className="checklist-grid mt-16">
          {legends.map((item) => {
            const isChecked = !!legendChecked[item.name];
            return (
              <div
                key={item.name}
                className={`checklist-item${isChecked ? " checked" : ""}`}
                onClick={() => toggleLegend(item.name)}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleLegend(item.name)}
                  onClick={(e) => e.stopPropagation()}
                />
                <img
                  src={item.icon}
                  alt={item.name}
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                />
                <span>{item.name}</span>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === "weapon" && (
        <div className="checklist-grid mt-16">
          {weapons.map((item) => {
            const isChecked = !!weaponChecked[item.name];
            return (
              <div
                key={item.name}
                className={`checklist-item${isChecked ? " checked" : ""}`}
                onClick={() => toggleWeapon(item.name)}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleWeapon(item.name)}
                  onClick={(e) => e.stopPropagation()}
                />
                <img src={item.icon} alt={item.name} style={{ objectFit: "contain" }} />
                <span>{item.name}</span>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === "combo" && (
        <div className="checklist-combo-grid mt-16">
          {legendWeapons.map((legend) => {
            const legendState = comboChecked[legend.name] || {};
            const bothChecked = legend.weapons.every((w) => !!legendState[w]);
            return (
              <div key={legend.name} className={`checklist-combo-card${bothChecked ? " complete" : ""}`}>
                <div className="checklist-combo-header">
                  <img
                    src={legend.icon}
                    alt={legend.name}
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                  />
                  <span>{legend.name}</span>
                </div>
                {legend.weapons.map((weaponName) => {
                  const isChecked = !!legendState[weaponName];
                  return (
                    <div
                      key={weaponName}
                      className={`checklist-combo-weapon-row${isChecked ? " weaponChecked" : ""}`}
                      onClick={() => toggleComboWeapon(legend.name, weaponName)}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleComboWeapon(legend.name, weaponName)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <img src={weaponIconLookup[weaponName] || ""} alt={weaponName} />
                      <span>{weaponName}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {resetModalOpen && (
        <div className="modal-overlay" onClick={() => setResetModalOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="text-danger uppercase mb-10" style={{ fontSize: 12, letterSpacing: 2 }}>
              Confirm Reset
            </div>
            <h3 style={{ margin: "0 0 8px 0", fontSize: 24 }}>Reset checklist?</h3>
            <p className="text-muted mb-0" style={{ lineHeight: 1.5 }}>
              This will clear every checked box on the "{tabLabels[activeTab]}" tab. This can't be undone.
            </p>
            <div className="flex gap-10 mt-20" style={{ justifyContent: "flex-end" }}>
              <button className="btn btn--secondary" onClick={() => setResetModalOpen(false)}>Cancel</button>
              <button className="btn btn--danger" onClick={confirmReset}>Yes, Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
